import Flickity from 'flickity';
import { getBreakpoint, onLoadComplete, getIndexedHashProperty } from './_utils';
import { scrollToElement } from './scrollToQueryHash';
import _throttle from 'lodash/throttle';

let indexedHashProperty = {};

const cssClass = 'WgTabContainer';
const classes = {
	activeTab: `${cssClass}-tab--active`,
	activePage: `${cssClass}-page--active`,
};
const selectors = {
	tabContainer: `.${cssClass}`,
	page: `.${cssClass}-page`,
	tab: `.${cssClass}-tab`,
	tabsContainer: `.${cssClass}-tabs-container`,
	tabs: `.${cssClass}-tabs`,
	leftArrow: `.${cssClass}-tabs-arrow-left`,
	rightArrow: `.${cssClass}-tabs-arrow-right`,
};

const scrollDistance = 100; // Adjust this value to change the scroll amount per click

const getAllIds = $tabContainer => {
	const $allTabs = $tabContainer.find(selectors.tab);
	const allIds = $allTabs.map((i, tab) => tab.dataset.id).toArray();
	return allIds;
};

const setActive = (id, $tabContainer) => {
	// Remove active class from all non-selected tabs / pages
	const $inactivePages = $tabContainer.find(`${selectors.page}:not([data-id=${id}])`);
	const $inactiveTabs = $tabContainer.find(`${selectors.tab}:not([data-id=${id}])`);
	const openTab = $tabContainer.find('.showall');
	openTab && openTab.removeClass('showall');
	const loadButton = $tabContainer.find('.ExpandableCardGrid-button-hide');
	loadButton && loadButton.removeClass('ExpandableCardGrid-button-hide');
	$inactivePages.removeClass(classes.activePage);
	$inactivePages.attr('aria-hidden', true);
	$inactiveTabs.removeClass(classes.activeTab);
	$inactiveTabs.attr('aria-selected', false);

	// Add active class to all non-selected tabs / pages
	const $activePage = $tabContainer.find(`${selectors.page}[data-id=${id}]`);
	const $activeTab = $tabContainer.find(`${selectors.tab}[data-id=${id}]`);
	$activePage.addClass(classes.activePage);
	$activePage.attr('aria-hidden', false);
	$activeTab.addClass(classes.activeTab);
	$activeTab.attr('aria-selected', true);

	// Update components that need to be updated/re-initialized
	updateFlickityComponents($tabContainer);
};

const updateFlickityComponents = $tabContainer => {
	const tabContainerFlickityCarousels = $tabContainer[0].querySelectorAll('.flickity-enabled');
	[...tabContainerFlickityCarousels].forEach(flickityCarousel => {
		const flickity = new Flickity(flickityCarousel);
		flickity.resize();
	});
};

const handleTabClick = e => {
	const $clickedTab = $(e.currentTarget);
	const $tabs = $clickedTab.closest(selectors.tabs);

	if ($clickedTab.length > 0) {
		const bp = getBreakpoint();
		if (bp === 'small' || bp === 'medium') {
			$clickedTab[0].scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'center',
			});
		}

		const $tabContainer = $clickedTab.closest(selectors.tabContainer);
		const activeId = $clickedTab.data('id');
		setActive(activeId, $tabContainer);

		// Ensure tab is fully in view
		ensureTabInView($clickedTab, $tabs);
	}
};

const handleTabFocus = e => {
	const $focusedTab = $(e.currentTarget);
	const $tabs = $focusedTab.closest(selectors.tabs);

	// Ensure tab is fully in view
	ensureTabInView($focusedTab, $tabs);
};

const ensureTabInView = ($tab, $tabs) => {
	// Calculate the sum of widths of previous sibling tabs
	var sum = 0;
	$tab.prevAll().each(function () {
		sum += $(this).width();
	});

	// Get the total scrollable width of the tabs container
	var get = $tabs[0].scrollWidth;

	// Calculate the scroll distance to center the tab
	var dist = sum - ($tabs.width() - $tab.width()) / 2;

	// Ensure the scroll distance is not negative
	if (dist < 0) {
		dist = 0;
	}
	// Uncomment and adjust if needed to handle scrolling beyond the container's width
	else if (dist + sum > get) {
		dist = get - sum + dist + dist;
	}

	// Animate the scrolling to the calculated distance
	$tabs.animate(
		{
			scrollLeft: dist,
		},
		300
	);
};

const addEventListeners = $tabContainer => {
	const $allTabs = $tabContainer.find(selectors.tab);

	// Add event listeners
	$allTabs.on('click', handleTabClick);
	$allTabs.on('focus', handleTabFocus); // Add focus event listener

	// Initialize scrollable tabs and arrows
	initializeScrollableTabs($tabContainer);
};

const initializeScrollableTabs = $tabContainer => {
	const $tabsContainer = $tabContainer.find(selectors.tabsContainer);
	const $tabs = $tabsContainer.find(selectors.tabs);
	const $leftArrow = $tabsContainer.find(selectors.leftArrow);
	const $rightArrow = $tabsContainer.find(selectors.rightArrow);

	function updateArrows() {
		const scrollLeft = $tabs.scrollLeft();
		const maxScrollLeft = $tabs[0].scrollWidth - $tabs.outerWidth();

		if (getBreakpoint() === 'large' || getBreakpoint() === 'xlarge') {
			if (scrollLeft === 0) {
				$leftArrow.show();
				$rightArrow.show();
			} else {
				$leftArrow.show();
				$rightArrow.show();
			}

			if (scrollLeft >= maxScrollLeft) {
				$rightArrow.show();
			} else {
				$rightArrow.show();
			}
		} else {
			$leftArrow.hide();
			$rightArrow.hide();
		}
	}

	function checkOverflow() {
		const isOverflowing = $tabs[0].scrollWidth > $tabs.outerWidth();
		if (isOverflowing) {
			updateArrows();
		} else {
			$leftArrow.hide();
			$rightArrow.hide();
		}
	}

	$tabs.on('scroll', updateArrows);
	$(window).on('resize', checkOverflow);

	$leftArrow.on(
		'click',
		_throttle(function () {
			$tabs.animate({ scrollLeft: $tabs.scrollLeft() - scrollDistance }, 300, 'linear');
		}, 300)
	);

	$rightArrow.on(
		'click',
		_throttle(function () {
			$tabs.animate({ scrollLeft: $tabs.scrollLeft() + scrollDistance }, 300, 'linear');
		}, 300)
	);

	// Initial check
	checkOverflow();
};

const initializeTabContainer = (i, element) => {
	// Find DOM elements
	const $tabContainer = $(element);
	const allIds = getAllIds($tabContainer);

	onLoadComplete(() => {
		addEventListeners($tabContainer);

		const tabId = $tabContainer.attr('id');
		if (indexedHashProperty && tabId === indexedHashProperty.id) {
			scrollToElement($tabContainer);

			// Set requested page to active
			let activeIndex = indexedHashProperty.index - 1;
			if (activeIndex >= allIds.length) {
				activeIndex = 0;
			}
			setActive(allIds[activeIndex], $tabContainer);
		}
	});

	// Set first page to active
	setActive(allIds[0], $tabContainer);
};

export default function initializeTabContainers() {
	indexedHashProperty = getIndexedHashProperty();
	const $allTabContainers = $(selectors.tabContainer);
	$allTabContainers.each(initializeTabContainer);
}
